import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Pencil, Copy, ExternalLink, MoreHorizontal, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { buildTrackingLink } from "@/lib/order-helpers";
import { OrderDialog } from "@/components/orders/OrderDialog";
import { OrderDetailData } from "@/types/orders";
 import { useOrderStatusMutation } from "@/hooks/useOrderStatusMutation";
import {
  ORDER_STATUS_LABELS,
  ORDER_STATUS_COLORS,
  ORDER_TYPE_LABELS,
  ORDER_TYPE_COLORS,
  FULFILLMENT_TYPE_LABELS,
  FULFILLMENT_TYPE_COLORS,
  OrderStatus,
  OrderType,
  FulfillmentType,
} from "@/config/order-constants";

interface OrderHeaderProps {
  order: OrderDetailData;
}

export function OrderHeader({ order }: OrderHeaderProps) {
  const navigate = useNavigate();
  
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

   // Use centralized status mutation with email notification
   const cancelOrderMutation = useOrderStatusMutation({
     onSuccess: () => {
      toast.success("Tilaus peruutettu");
    },
  });

  const copyTrackingLink = () => {
    if (order.tracking_token) {
      const link = buildTrackingLink(order.order_number, order.tracking_token);
      navigator.clipboard.writeText(link);
      toast.success("Seurantalinkki kopioitu");
    }
  };

  return (
    <>
      <div className="space-y-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/orders")}
          className="gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Takaisin tilauksiin
        </Button>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="space-y-2">
            <h1 className="text-2xl sm:text-3xl font-bold">
              Tilaus #{order.order_number}
            </h1>
            <div className="flex flex-wrap items-center gap-2">
              <Badge
                variant="outline"
                className={cn(ORDER_TYPE_COLORS[order.order_type as OrderType])}
              >
                {order.order_type === "new_work" 
                  ? "🔨" 
                  : order.order_type === "field_work" 
                    ? "🔧" 
                    : "🔄"}{" "}
                {ORDER_TYPE_LABELS[order.order_type as OrderType]}
              </Badge>
              <Badge
                variant="outline"
                className={cn(FULFILLMENT_TYPE_COLORS[order.fulfillment_type as FulfillmentType])}
              >
                {FULFILLMENT_TYPE_LABELS[order.fulfillment_type as FulfillmentType]}
              </Badge>
              <Badge className={cn(ORDER_STATUS_COLORS[order.status as OrderStatus])}>
                {ORDER_STATUS_LABELS[order.status as OrderStatus]}
              </Badge>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button onClick={() => setIsDialogOpen(true)}>
              <Pencil className="h-4 w-4 mr-2" />
              Muokkaa
            </Button>
            {order.tracking_token && (
              <Button variant="outline" onClick={copyTrackingLink}>
                <Copy className="h-4 w-4 mr-2" />
                Kopioi seurantalinkki
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {order.tracking_token && (
                  <DropdownMenuItem
                    onClick={() =>
                      window.open(
                        `/track/${order.order_number}/${order.tracking_token}`,
                        "_blank"
                      )
                    }
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Avaa seurantasivu
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive"
                  onClick={() => setShowCancelConfirm(true)}
                  disabled={order.status === "cancelled"}
                >
                  <X className="h-4 w-4 mr-2" />
                  Peru tilaus
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      <OrderDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        orderId={order.id}
      />

      {/* Cancel Order Confirmation Dialog */}
      <AlertDialog open={showCancelConfirm} onOpenChange={setShowCancelConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Peru tilaus?</AlertDialogTitle>
            <AlertDialogDescription>
              Haluatko varmasti peruuttaa tilauksen #{order.order_number}? 
              Peruutettu tilaus siirtyy arkistoon eikä sitä voi enää aktivoida.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Älä peruuta</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                 cancelOrderMutation.mutate({
                   orderId: order.id,
                   newStatus: "cancelled",
                   oldStatus: order.status,
                 });
                setShowCancelConfirm(false);
              }}
            >
              Peru tilaus
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
